import React, { useState } from 'react';
import { Square, CheckSquare, AlertCircle, ShieldAlert, Calendar } from 'lucide-react';
import { CustomerCustomTodo } from '../../../shared/types';

interface TodoSectionProps {
  todos: CustomerCustomTodo[];
  customerName: string;
  onConfirmationChange?: (todoId: string, confirmed: boolean) => void;
  confirmedTodos?: string[];
  showConfirmation?: boolean;
}

export const TodoSection: React.FC<TodoSectionProps> = ({ 
  todos, 
  customerName,
  onConfirmationChange,
  confirmedTodos = [],
  showConfirmation = false
}) => {
  // Lokaler State für UI-Updates
  const [localConfirmed, setLocalConfirmed] = useState<string[]>(confirmedTodos);

  // Wenn keine To-Dos vorhanden sind, nichts anzeigen
  if (!todos || todos.length === 0) {
    return null;
  }

  // Filtere To-Dos die eine Bestätigung erfordern
  const todosRequiringConfirmation = todos.filter(todo => todo.requires_confirmation);
  const hasConfirmationRequired = todosRequiringConfirmation.length > 0;

  const handleToggleConfirmation = (todoId: string) => {
    const isConfirmed = localConfirmed.includes(todoId);
    const newConfirmed = isConfirmed 
      ? localConfirmed.filter(id => id !== todoId)
      : [...localConfirmed, todoId];
    
    setLocalConfirmed(newConfirmed);
    
    if (onConfirmationChange) {
      onConfirmationChange(todoId, !isConfirmed);
    }
  };

  // Helper für Monats-Anzeige
  const getMonthsDisplay = (activeMonths: number[] | null): string => {
    if (!activeMonths || activeMonths.length === 0) return '';
    
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    
    if (activeMonths.length === 12) return '';
    if (activeMonths.length > 6) {
      return `Aktiv in ${activeMonths.length} Monaten`;
    }
    
    return `Nur in: ${activeMonths
      .sort((a, b) => a - b)
      .map(m => monthNames[m - 1])
      .join(', ')}`;
  };

  return (
    <div style={{ 
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: hasConfirmationRequired && showConfirmation ? '#FEF3C7' : '#F0F9FF',
      borderRadius: '8px',
      border: hasConfirmationRequired && showConfirmation ? '1px solid #F59E0B' : '1px solid #0EA5E9'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        marginBottom: '12px'
      }}>
        {hasConfirmationRequired && showConfirmation ? (
          <ShieldAlert size={20} color="#F59E0B" />
        ) : (
          <AlertCircle size={20} color="#0EA5E9" />
        )}
        <h4 style={{ 
          margin: 0, 
          fontSize: '16px', 
          fontWeight: '600',
          color: hasConfirmationRequired && showConfirmation ? '#92400E' : '#0C4A6E'
        }}>
          To-Dos für {customerName}
        </h4>
      </div>

      {/* Info Text */}
      <p style={{ 
        fontSize: '14px', 
        color: hasConfirmationRequired && showConfirmation ? '#B45309' : '#0369A1', 
        margin: '0 0 16px 0',
        lineHeight: '1.4'
      }}>
        {hasConfirmationRequired && showConfirmation
          ? 'Bitte bestätigen Sie alle erforderlichen Punkte vor dem Versand:'
          : 'Bitte überprüfen Sie folgende Punkte vor dem Monatsabschluss:'}
      </p>

      {/* To-Do Liste */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {todos.map((todo) => (
          <TodoItem 
            key={todo.id} 
            todo={todo}
            isConfirmed={localConfirmed.includes(todo.id)}
            onToggleConfirmation={showConfirmation ? handleToggleConfirmation : undefined}
            monthsDisplay={getMonthsDisplay(todo.active_months || null)}
          />
        ))}
      </div>

      {/* Warnung wenn nicht alle bestätigt */}
      {hasConfirmationRequired && showConfirmation && (
        <div style={{ 
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#FED7AA',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#7C2D12'
        }}>
          {todosRequiringConfirmation.every(todo => localConfirmed.includes(todo.id))
            ? '✓ Alle erforderlichen To-Dos wurden bestätigt'
            : `⚠️ ${todosRequiringConfirmation.filter(todo => !localConfirmed.includes(todo.id)).length} von ${todosRequiringConfirmation.length} bestätigungspflichtigen To-Dos müssen noch bestätigt werden`}
        </div>
      )}
      
      {/* Standard-Hinweis */}
      {!showConfirmation && (
        <div style={{ 
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#E0F2FE',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#0C4A6E'
        }}>
          💡 Diese To-Dos sind kundenspezifische Erinnerungen. Sie können in den Kundendetails verwaltet werden.
        </div>
      )}
    </div>
  );
};

interface TodoItemProps {
  todo: CustomerCustomTodo;
  isConfirmed?: boolean;
  onToggleConfirmation?: (todoId: string) => void;
  monthsDisplay?: string;
}

const TodoItem: React.FC<TodoItemProps> = ({ 
  todo, 
  isConfirmed = false,
  onToggleConfirmation,
  monthsDisplay
}) => {
  const canBeChecked = todo.requires_confirmation && onToggleConfirmation;

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'flex-start', 
      gap: '12px',
      padding: '8px',
      backgroundColor: canBeChecked ? (isConfirmed ? '#D1FAE5' : '#FEF3C7') : 'transparent',
      borderRadius: '6px',
      transition: 'background-color 0.2s ease'
    }}>
      {/* Checkbox/Icon */}
      <div style={{ 
        marginTop: '2px',
        color: canBeChecked 
          ? (isConfirmed ? '#10B981' : '#F59E0B')
          : '#0369A1',
        cursor: canBeChecked ? 'pointer' : 'default'
      }}
        onClick={() => canBeChecked && onToggleConfirmation(todo.id)}
      >
        {canBeChecked ? (
          isConfirmed ? <CheckSquare size={16} /> : <Square size={16} />
        ) : (
          <Square size={16} />
        )}
      </div>

      {/* To-Do Content */}
      <div style={{ flex: 1 }}>
        <div style={{ 
          fontSize: '14px', 
          fontWeight: '500',
          color: canBeChecked 
            ? (isConfirmed ? '#065F46' : '#92400E')
            : '#0C4A6E',
          marginBottom: '2px',
          textDecoration: isConfirmed ? 'line-through' : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {todo.title}
          {todo.requires_confirmation && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              padding: '1px 4px',
              backgroundColor: '#FEF3C7',
              color: '#92400E',
              borderRadius: '3px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              <ShieldAlert size={10} />
              BESTÄTIGUNG
            </span>
          )}
          {monthsDisplay && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              padding: '1px 4px',
              backgroundColor: '#E0E7FF',
              color: '#3730A3',
              borderRadius: '3px',
              fontSize: '10px'
            }}>
              <Calendar size={10} />
              {monthsDisplay}
            </span>
          )}
        </div>
        
        {todo.description && (
          <div style={{ 
            fontSize: '13px', 
            color: canBeChecked 
              ? (isConfirmed ? '#10B981' : '#B45309')
              : '#0369A1',
            lineHeight: '1.3',
            opacity: isConfirmed ? 0.7 : 1
          }}>
            {todo.description}
          </div>
        )}
      </div>
    </div>
  );
};